import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { Text, Flex, useToast, Skeleton, Switch, Button } from '@chakra-ui/react';
import { PageContent } from 'src/components/PageContent';
import { SectionBox } from 'src/components/SectionBox';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { breadcrumb } from './breadcrumb';
import { GetParamsObject } from '@/model/query-params';
import { useFiltersForm } from '@/utils/useFiltersForm';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import { BaladaApp } from '@/model';
import { BaladaAppFilterBox } from './components/BaladaAppFilterBox';
import {
  useBaladaApp,
  useDeleteBaladaAppMutation,
  useGetBaladaAppByUUID,
} from '@/services/queries/balada-app';
import { BaladaAppFilters } from './types';
import { BaladaAppTable } from './components/BaladaAppTable/BaladaAppTable';
import { BaladaAppEditModal } from './components/BaladaAppEditModal';
import { BaladaAppCreateModal } from './components/BaladaAppCreateModal';
import {
  useAppApplicationComponentArea,
  useEnableApplicationComponentMutation,
} from '@/services/queries/admin-config';
import { ApplicationComponentId } from '@/model/application-component';
import { useTranslation } from 'react-i18next';

export const ApplicationBaladaApp = () => {
  const { t } = useTranslation();
  const toast = useToast();

  const [isAppAreaActive, setIsAppAreaActive] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<BaladaApp | undefined>(undefined);

  const { data: appAreaData, isLoading: isLoadingAppArea } = useAppApplicationComponentArea(
    ApplicationComponentId.BALADA_APP,
  );
  const { mutate: enableAppAreaMutation, isLoading: isLoadingEnableOrDisableAppArea } =
    useEnableApplicationComponentMutation();
  const [pendingAppAreaState, setPendingAppAreaState] = useState<boolean | null>(null);
  const [isDialogAppAreaState, setIsDialogAppAreaState] = useState(false);

  const cancelAppAreaChange = () => {
    setIsDialogAppAreaState(false);
    setPendingAppAreaState(null);
  };

  const handleAppAreaChange = (event: ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setPendingAppAreaState(isChecked);
    setIsDialogAppAreaState(true);
  };

  const confirmAppAreaChange = () => {
    if (pendingAppAreaState === null) return;

    setIsAppAreaActive(pendingAppAreaState);

    enableAppAreaMutation([{ component_id: ApplicationComponentId.BALADA_APP, active: pendingAppAreaState }], {
      onSuccess: () => {
        toast({
          status: 'success',
          title: pendingAppAreaState
            ? t('pages.application_balada_app.balada_app_area_enabled_success')
            : t('pages.application_balada_app.balada_app_area_disabled_success'),
        });
      },
      onError: () => {
        toast({
          status: 'error',
          title: pendingAppAreaState
            ? t('pages.application_balada_app.balada_app_area_enable_error')
            : t('pages.application_balada_app.balada_app_area_disable_error'),
        });
        setIsAppAreaActive(!pendingAppAreaState);
      },
    });
    setIsDialogAppAreaState(false);
    setPendingAppAreaState(null);
  };

  const deleteMutation = useDeleteBaladaAppMutation();
  const getItemByUUID = useGetBaladaAppByUUID();

  const filtersForm = useFiltersForm<BaladaAppFilters>({
    defaultValues: {
      title: '',
      type: '',
      start_date: '',
      end_date: '',
    },
  });
  const filterData = filtersForm.watch();

  const [sorting, setSorting] = useState<{ field: string; desc: boolean }>({ field: 'created_at', desc: true });
  const [pagination, setPagination] = useState<{ pageNumber: number; pageSize: number }>({
    pageNumber: 1,
    pageSize: 10,
  });

  const params = GetParamsObject(
    filterData,
    pagination.pageNumber,
    pagination.pageSize,
    sorting.desc ? 'desc' : 'asc',
    sorting.field,
  );

  const { data: itemData, isLoading, forceRefetch } = useBaladaApp(params);
  const itemsData = itemData?.data || [];

  const onHandleDelete = (item: BaladaApp) => {
    setSelectedItem(item);
    setIsConfirmModalOpen(true);
  };

  const onHandleDeleteConfirm = () => {
    if (!selectedItem?.uuid) return;

    setIsConfirmModalOpen(false);

    deleteMutation.mutate(selectedItem.uuid, {
      onSuccess: () => {
        toast({
          title: t('pages.application_balada_app.balada_app_deleted_success'),
          status: 'success',
        });
        forceRefetch();
        setSelectedItem(undefined);
      },
      onError: (resp: any) => {
        setSelectedItem(undefined);
        toast({
          status: 'error',
          title: (resp && resp.message) || t('pages.application_balada_app.balada_app_delete_error'),
        });
      },
    });
  };

  const onHandleEdit = (uuid: string) => {
    getItemByUUID.mutate(uuid, {
      onSuccess: res => {
        setSelectedItem(res);
        setIsEditModalOpen(true);
      },
      onError: () => {
        toast({
          status: 'error',
          title: t('pages.application_balada_app.balada_app_not_found'),
        });
      },
    });
  };

  const onHandleEditDone = useCallback(() => {
    setIsEditModalOpen(false);
    setSelectedItem(undefined);
    forceRefetch();
  }, [forceRefetch]);

  const onHandleCreateDone = useCallback(() => {
    setIsCreateModalOpen(false);
    forceRefetch();
  }, [forceRefetch, setIsCreateModalOpen]);

  useEffect(() => {
    if (!appAreaData) return;
    setIsAppAreaActive(appAreaData.is_active);
  }, [appAreaData]);

  return (
    <PageContent title={t('pages.application_balada_app.balada_app_page_title')} breadcrumbItems={breadcrumb}>
      <SectionBox bodyProps={{ gap: 'small' }}>
        <Flex direction={{ base: 'column', md: 'row' }} justifyContent={{ md: 'space-between' }} gap={4}>
          <Flex alignItems='center' gap={4}>
            {isLoadingAppArea ? (
              <>
                <Skeleton w='130px' h='21px' />
                <Skeleton w='102px' h='21px' />
              </>
            ) : (
              <>
                <Text fontWeight='bold' color='brand.500'>
                  {t('pages.application_balada_app.balada_app_label')}
                </Text>
                <Switch size='sm' isChecked={isAppAreaActive} onChange={handleAppAreaChange}>
                  {isAppAreaActive
                    ? t('pages.application_balada_app.balada_app_enabled')
                    : t('pages.application_balada_app.balada_app_disabled')}
                </Switch>
              </>
            )}
          </Flex>

          <Button
            display='flex'
            alignItems='center'
            gap={2}
            onClick={() => setIsCreateModalOpen(true)}
            width={{ base: '100%', md: 'initial' }}
          >
            <ChakraFeatherIcon name='plus-circle' />
            {t('pages.application_balada_app.balada_app_create_button')}
          </Button>
        </Flex>

        <Text fontSize='sm' color='gray.600'>
          {t('pages.application_balada_app.balada_app_description')}
        </Text>

        <SectionBox bodyProps={{ gap: 'small' }}>
          <BaladaAppFilterBox filtersForm={filtersForm} forceRefetch={forceRefetch} />
        </SectionBox>

        <Flex direction='column' gap='deka' mt={8}>
          <BaladaAppTable
            data={itemsData}
            isLoading={isLoading}
            sorting={sorting}
            pageNumber={pagination.pageNumber}
            pageSize={pagination.pageSize}
            totalPages={itemData?.meta.pages || 0}
            totalItems={itemData?.meta.total || 0}
            onSortingChange={(field, desc) => setSorting({ field, desc })}
            onPaginationChange={(pageNumber, pageSize) => setPagination({ pageNumber, pageSize })}
            onEditClick={item => onHandleEdit(item.uuid)}
            onDeleteClick={item => onHandleDelete(item)}
          />
        </Flex>
      </SectionBox>

      {selectedItem && (
        <BaladaAppEditModal
          isOpen={!!isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedItem(undefined);
          }}
          item={selectedItem}
          onDone={onHandleEditDone}
        />
      )}

      <BaladaAppCreateModal
        isOpen={!!isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onDone={onHandleCreateDone}
      />

      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={() => {
          setIsConfirmModalOpen(false);
          setSelectedItem(undefined);
        }}
        onConfirm={onHandleDeleteConfirm}
        isLoading={false}
        title={t('pages.application_balada_app.balada_app_delete_title')}
        description={
          <Text>
            {t('pages.application_balada_app.balada_app_delete_description')}{' '}
            <Text as='span' fontWeight='bold'>
              "{selectedItem?.title}"
            </Text>
            ?
          </Text>
        }
      />

      <ConfirmationModal
        isOpen={isDialogAppAreaState}
        onClose={cancelAppAreaChange}
        onConfirm={confirmAppAreaChange}
        title={t('pages.application_balada_app.balada_app_component_title')}
        description={
          <Text>
            {pendingAppAreaState
              ? t('pages.application_balada_app.balada_app_component_description_enabled')
              : t('pages.application_balada_app.balada_app_component_description_disabled')}
          </Text>
        }
      />

      <CenteredSpinner isLoading={getItemByUUID.isLoading || isLoadingEnableOrDisableAppArea || deleteMutation.isLoading} />
    </PageContent>
  );
};
